<template>
  <div
    v-if="modelValue"
    class="iq-overlay"
    :class="{ 'mdp-577-transcript': responsibilitySuggestionTranscriptEnabled }"
  >
    <div class="iq-overlay-backdrop" @click="requestClose"></div>

    <input
      v-if="isPositionImport"
      ref="positionImportFileInput"
      type="file"
      accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      class="position-import-file-input"
      data-testid="position-import-file-input"
      tabindex="-1"
      aria-label="Upload job description document"
      @change="handlePositionImportFileSelected"
    />

    <div class="iq-overlay-shell">
      <IQSidebar
        embedded
        controlledSelection
        :selectedChatId="selectedSidebarConversationId"
        :todaySuggestions="conversationBuckets.today"
        :previous7DaysSuggestions="conversationBuckets.previous7"
        :previous30DaysSuggestions="conversationBuckets.previous30"
        @suggestion-selected="handleSidebarConversationSelect"
        @edit-click="handleSidebarNewChat"
      />

      <div class="iq-overlay-main">
        <!-- Header -->
        <div class="iq-overlay-header">
          <IQControlledOverlayHeader :title="overlayHeaderTitle" @close="requestClose" />
        </div>

        <!-- Content Area - Split when showSuggestions, showCheckpointSuggestions, or showVideoInstructionsPane is true -->
        <div
          class="iq-overlay-body"
          :class="{
            'split-layout':
              showSuggestions ||
              showCheckpointSuggestions ||
              iqOverlayStore.showVideoInstructionsPane ||
              showDocumentPreview,
          }"
        >
          <!-- Left Side: Chat -->
          <div
            class="iq-chat-section"
            :class="{
              'with-suggestions': showSuggestions || showCheckpointSuggestions,
              'with-document-preview': showDocumentPreview,
              'with-video-instructions':
                iqOverlayStore.showVideoInstructionsPane && !hasVideoInstructionsError,
              'with-video-instructions-error':
                iqOverlayStore.showVideoInstructionsPane && hasVideoInstructionsError,
            }"
          >
            <!-- Initial Welcome State -->
            <div v-if="showWelcomeState" class="iq-welcome-container">
              <div class="iq-welcome-content">
                <IQAskAnythingHeader :text="welcomeText" />
                <div class="iq-search-center">
                  <AskAQuestionPrompt
                    v-if="usesSuggestChatInput"
                    class="iq-suggest-chat-input"
                    :placeholder="suggestChatInputPlaceholder"
                    :disabled="chatStore.isSuggestionStreamActive"
                    :generating="chatStore.isSuggestionStreamActive"
                    @submit="handleSubmit"
                    @stop="handleStop"
                  />
                  <IQSearch
                    v-else
                    @submit="handleSubmit"
                    :isGenerating="chatStore.isGenerating || chatStore.isLoading"
                    @stop="handleStop"
                  />
                </div>
              </div>
            </div>

            <!-- Chat State -->
            <div v-else class="iq-chat-container">
              <!-- Pinned uploaded-file card (ER-11580): rendered above the scrollable
                   messages so the chat scrolls independently beneath it. -->
              <div v-if="isPositionImport" class="iq-uploaded-file-card">
                <slot name="uploadedFileCard" />
              </div>

              <div class="iq-chat-messages" ref="messagesContainer">
                <!-- Chat Messages -->
                <div
                  v-for="message in chatStore.messages"
                  :key="message.id"
                  class="iq-message-wrapper"
                  :class="message.type"
                >
                  <IQUserPrompt
                    v-if="message.type === 'user'"
                    :message="message.content"
                    :attachment-refs="message.attachmentRefs || []"
                    variant="light"
                    @preview-image="openImagePreview"
                  />
                  <IQAIResponse
                    v-else-if="message.type === 'ai'"
                    :response="message.content"
                    format="markdown"
                    :isThinking="false"
                    :currentReasoning="''"
                    surface="light"
                    @actionClick="handleActionClick"
                  />
                </div>

                <!-- Custom IQLoader positioned under the last user message when loading (hidden during thinking phase) -->
                <div
                  v-if="
                    shouldUseCustomLoader &&
                    loaderVisible &&
                    chatStore.isLoading &&
                    !chatStore.currentResponse &&
                    !chatStore.isThinking &&
                    chatStore.messages.length > 0
                  "
                  class="iq-loader-wrapper"
                >
                  <IQLoader
                    :loading="loadingProgress"
                    :modelValue="loaderVisible"
                    @stop="handleLoaderStop"
                    @update:modelValue="loaderVisible = $event"
                  />
                </div>

                <!-- Pulse Loader (simple loader for manual prompts, hidden during thinking phase) -->
                <div
                  v-if="
                    !shouldUseCustomLoader &&
                    chatStore.isLoading &&
                    !chatStore.currentResponse &&
                    !chatStore.isThinking
                  "
                  class="iq-loading-wrapper"
                >
                  <div class="iq-pulse-loader">
                    <div class="pulse-circle"></div>
                  </div>
                </div>

                <!-- Current Thinking/Response (Gemini-style thinking animation) -->
                <div
                  v-if="
                    chatStore.isThinking || (chatStore.isGenerating && chatStore.currentResponse)
                  "
                  class="iq-message-wrapper"
                >
                  <IQAIResponse
                    :response="chatStore.currentResponse"
                    :isThinking="chatStore.isThinking"
                    :currentReasoning="chatStore.currentReasoning"
                    surface="light"
                    @actionClick="handleActionClick"
                  />
                </div>
              </div>

              <!-- Inline editor for Improve (Rule 15/16) -->
              <div
                v-if="isPositionImport && positionImportEditing"
                class="position-import-inline-editor"
                data-testid="position-import-inline-editor"
              >
                <MemoInputWithLimit
                  v-model="positionImportEditText"
                  label="Responsibility"
                  placeholder="Enter responsibility text"
                  :min="1"
                  :max="1000"
                  :rows="4"
                  :auto-grow="false"
                  :errorMessage="positionImportEditError"
                  class="position-import-inline-editor__input"
                />
                <div class="position-import-inline-editor__actions">
                  <StandardButton
                    type="magenta"
                    variant="outline"
                    size="small"
                    label="Cancel"
                    :disabled="positionImportBusy"
                    @click="handlePositionImportEditCancel"
                  />
                  <StandardButton
                    type="magenta"
                    variant="primary"
                    size="small"
                    label="Save"
                    :disabled="positionImportBusy"
                    :loaderRight="positionImportBusy"
                    @click="handlePositionImportEditSave"
                  />
                </div>
              </div>

              <!-- Search Input at Bottom (only in chat state) -->
              <div v-if="!isPositionImport" class="iq-search-container">
                <AskAQuestionPrompt
                  v-if="usesSuggestChatInput"
                  class="iq-suggest-chat-input"
                  :placeholder="suggestChatInputPlaceholder"
                  :disabled="chatStore.isSuggestionStreamActive"
                  :generating="chatStore.isSuggestionStreamActive"
                  @submit="handleSubmit"
                  @stop="handleStop"
                />
                <IQSearch
                  v-else
                  @submit="handleSubmit"
                  :isGenerating="chatStore.isGenerating || chatStore.isLoading"
                  @stop="handleStop"
                />
              </div>
            </div>
          </div>

          <!-- Right Side: Suggestions Navigator (for Role Responsibilities) -->
          <div
            v-if="showSuggestions"
            class="iq-suggestions-section"
            :class="{ 'all-done': isAllDone && !isAwaitingRegeneratedSuggestions }"
            id="iq-navigator-container"
          >
            <div
              v-if="isAwaitingRegeneratedSuggestions"
              class="iq-suggestions-regenerating"
              data-testid="iq-suggestions-regenerating"
            >
              <div class="iq-suggestions-regenerating__pulse" aria-hidden="true"></div>
              <p class="iq-suggestions-regenerating__text">IQ is updating suggestions…</p>
            </div>
            <SuggestResponsibilitiesNavigator
              v-else
              :modelValue="true"
              :roleName="roleName"
              :responsibilities="suggestedResponsibilities"
              :inline="true"
              :isCreating="iqOverlayStore.isCreatingResponsibility"
              :creationError="iqOverlayStore.creationError || ''"
              @close="handleNavigatorClose"
              @create-and-attach="handleCreateAndAttach"
              @reject="handleReject"
              @suggest-more="handleSuggestMore"
              @edit="handleEdit"
              @is-all-done="handleIsAllDone"
            />
          </div>

          <!-- Right Side: Checkpoint Suggestions Navigator (for Responsibility Checkpoints) -->
          <div
            v-if="showCheckpointSuggestions"
            class="iq-suggestions-section"
            :class="{ 'all-done': isAllCheckpointsDone }"
            id="iq-checkpoint-navigator-container"
          >
            <SuggestCheckpointsNavigator
              ref="checkpointNavigatorRef"
              :modelValue="true"
              :inline="true"
              :responsibilityName="responsibilityName"
              :checkpoints="suggestedCheckpoints"
              :isCreating="iqOverlayStore.isCreatingCheckpoint"
              @close="handleCheckpointNavigatorClose"
              @suggest-more="handleSuggestMoreCheckpoints"
              @add-checkpoint="handleAddCheckpoint"
              @reject-checkpoint="handleRejectCheckpoint"
              @is-all-done="handleIsAllCheckpointsDone"
            />
          </div>

          <!-- Right Side: Video Instructions Pane -->
          <div
            v-if="iqOverlayStore.showVideoInstructionsPane && iqOverlayStore.suggestedInstructions"
            class="iq-video-instructions-section"
            :class="{ 'iq-video-instructions-section--error': hasVideoInstructionsError }"
          >
            <!-- Header with buttons only when validation passed (no error) -->
            <div v-if="!hasVideoInstructionsError" class="iq-video-instructions-header">
              <div class="iq-video-instructions-actions">
                <StandardButton
                  type="magenta"
                  variant="outline"
                  size="small"
                  label="Discard"
                  :disabled="isButtonLoading"
                  @click="handleVideoInstructionsDiscard"
                />
                <StandardButton
                  type="magenta"
                  variant="primary"
                  size="small"
                  label="Save"
                  :disabled="isButtonLoading"
                  :loading="isButtonLoading"
                  @click="handleVideoInstructionsSave"
                />
              </div>
            </div>
            <div class="iq-video-instructions-content">
              <MicrolearningViewEdit
                :video="iqOverlayStore.suggestedInstructions.video_url || iqOverlayStore.videoUrl"
                :error="iqOverlayStore.suggestedInstructions.error || null"
                :summary="iqOverlayStore.suggestedInstructions.summary || null"
                :steps="iqOverlayStore.suggestedInstructions.steps || []"
                :editable="true"
                @summary-click="handleSummaryClick"
                @step-click="handleStepClick"
              />
            </div>
          </div>

          <!-- Right Side: Import Job Description document preview (ER-11580) -->
          <div
            v-if="showDocumentPreview"
            class="iq-document-preview-section"
            data-testid="iq-document-preview-section"
          >
            <div class="iq-document-preview-header">
              <span class="iq-document-preview-title">Document preview</span>
              <button
                type="button"
                class="iq-document-preview-close"
                aria-label="Close document preview"
                data-testid="iq-document-preview-close"
                @click="emit('close-document-preview')"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div class="iq-document-preview-content">
              <slot name="documentPreview" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Inspection Step Dialog -->
    <InspectionStepDialog
      :value="showInspectionDialog"
      :state="inspectionDialogState"
      :category="dialogCategory"
      :inspect="dialogInspect"
      :lookFor="dialogLookFor"
      :passIf="dialogPassIf"
      :failIf="dialogFailIf"
      :callApiOnSave="false"
      @input="showInspectionDialog = $event"
      @close="handleInspectionDialogClose"
      @discard="handleInspectionDialogDiscard"
      @submit="handleInspectionDialogSubmit"
    />

    <IQSuggestOverlayExitConfirmDialog
      v-if="iqSuggestExitConfirmEnabled"
      :value="showExitConfirm"
      :suggestions-remaining="remainingSuggestions"
      @input="showExitConfirm = $event"
      @keep-reviewing="keepReviewing"
      @confirm-exit="confirmExit"
    />

    <!-- Attachment image preview: rendered at the overlay root so the modal is
         not trapped inside the chat body's isolation/stacking context (which
         would leave the header painted above the backdrop). -->
    <AskAQuestionAttachmentImagePreview
      :open="Boolean(imagePreview)"
      :image-src="imagePreview?.src || ''"
      :image-name="imagePreview?.name || 'Image preview'"
      @close="closeImagePreview"
    />
  </div>
</template>

<script setup>
import { computed, ref, nextTick, watch, onUnmounted } from "vue";
import IQControlledOverlayHeader from "@components/IQControlledOverlayHeader/IQControlledOverlayHeader.vue";
import IQAskAnythingHeader from "@components/IQAskAnythingHeader/IQAskAnythingHeader.vue";
import IQSearch from "@components/IQSearch/IQSearch.vue";
import AskAQuestionPrompt from "@components/AskAQuestionPrompt/AskAQuestionPrompt.vue";
import IQUserPrompt from "@components/IQUserPrompt/IQUserPrompt.vue";
import IQAIResponse from "@components/IQAIResponse/IQAIResponse.vue";
import IQLoader from "@components/IQLoader/IQLoader.vue";
import SuggestResponsibilitiesNavigator from "@components/SuggestResponsibilitiesNavigator/SuggestResponsibilitiesNavigator.vue";
import SuggestCheckpointsNavigator from "@components/SuggestCheckpointsNavigator/SuggestCheckpointsNavigator.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import InspectionStepDialog from "@components/InspectionStepDialog/InspectionStepDialog.vue";
import IQSidebar from "@components/IQSidebar/IQSidebar.vue";
import IQSuggestOverlayExitConfirmDialog from "@components/IQSuggestOverlayExitConfirmDialog/IQSuggestOverlayExitConfirmDialog.vue";
import AskAQuestionAttachmentImagePreview from "@components/AskAQuestionPrompt/AskAQuestionAttachmentImagePreview.vue";
import MemoInputWithLimit from "@components/MemoInputWithLimit/MemoInputWithLimit.vue";
import { useIQChatStore } from "./store/iqChatStore.js";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { useIQSuggestOverlayExitConfirm } from "./composables/useIQSuggestOverlayExitConfirm.js";
import {
  buildIQSuggestionPayload,
  buildPositionImportBenchmarkPayload,
  isControlledIQSuggestOverlayType,
  suggestionTypeForOverlayType,
  POSITION_IMPORT_SUGGESTION_TYPES,
} from "./buildIQSuggestionPayload.js";
import {
  createPositionImportSession,
  buildReadinessGateMessage,
  POSITION_IMPORT_ACTION,
  POSITION_IMPORT_PHASE,
} from "./positionImportFlow.js";
import { resetIQSuggestionSession } from "./resetIQSuggestionSession.js";
import {
  isAwaitingRegeneratedResponsibilitySuggestions,
  mapRoleResponsibilitySuggestions,
  shouldClearUnreviewedSuggestionsOnRefinement,
} from "./suggestionRefinement.js";
import { API } from "@/api/apiEndPoints.js";
import { fetchIQConversations, fetchIQConversationDetail, extractPositionImportResponsibilities, askIQStream } from "@/api/askIQApi";
import { bucketIQConversationsByRecency } from "./bucketIQConversations.js";
import { mapIQConversationDetailToChatMessages } from "./mapIQConversationDetailToChatMessages.js";
import configService from "../../account/src/services/config";
import {
  buildVideoToComponentsPayload,
  buildIqVideoSuggestEndpoint,
} from "@/utils/buildVideoToComponentsPayload";
import { changeCheckpointV2, saveVideoInspectionInstructions } from "@/api/responsibilityAPI";
import store from "../../account/src/store";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  welcomeText: {
    type: String,
    default: "What do you want to do today?",
  },
  initialPrompt: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    default: "",
  },
  showSuggestions: {
    type: Boolean,
    default: false,
  },
  roleName: {
    type: String,
    default: "",
  },
  suggestedResponsibilities: {
    type: Array,
    default: () => [],
  },
  useCustomLoader: {
    type: Boolean,
    default: undefined, // If not provided, will be determined by initialPrompt
  },
  // Checkpoint suggestion props
  showCheckpointSuggestions: {
    type: Boolean,
    default: false,
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  suggestedCheckpoints: {
    type: Array,
    default: () => [],
  },
  // Import Job Description document preview (ER-11580)
  // When true, the right-pane document preview is rendered. The pane's content
  // is provided via the `documentPreview` slot; the pane's close control emits
  // `close-document-preview`.
  showDocumentPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "close",
  "submit",
  "actionClick",
  "create-and-attach",
  "reject",
  "suggest-more",
  "edit",
  "close-navigator",
  "update:suggestions",
  // Checkpoint events (using store pattern for actions, only navigator/UI events via emit)
  "close-checkpoint-navigator",
  "update:checkpoint-suggestions",
  "edit-checkpoint",
  "refresh-checkpoint",
  // Import Job Description document preview (ER-11580)
  "close-document-preview",
  "document-uploaded",
]);

// Stores
const chatStore = useIQChatStore();
const iqOverlayStore = useIQOverlayStore();

// Refs
const messagesContainer = ref(null);
const loadingProgress = ref(0);
const progressInterval = ref(null);
const loaderVisible = ref(false);
const isAllDone = ref(false);
const isAllCheckpointsDone = ref(false);
const checkpointNavigatorRef = ref(null);
const isButtonLoading = ref(false);
// Attachment image preview (opened from a message's image card), rendered at
// the overlay root so it escapes the chat body's stacking context.
const imagePreview = ref(null);

// Import Job Description (positionImport) session state
const positionImportSession = ref(null);
const positionImportFileInput = ref(null);
const positionImportBusy = ref(false);
const positionImportEditing = ref(null); // { kind: "improvement" | "missing" }
const positionImportEditText = ref("");
const positionImportEditError = ref("");

const iqConversationsData = ref(null);
const iqConversationDetail = ref(null);
const selectedSidebarConversationId = ref(null);

const conversationBuckets = computed(() =>
  bucketIQConversationsByRecency(iqConversationsData.value?.conversations)
);

const showWelcomeState = computed(
  () => !chatStore.hasMessages && !selectedSidebarConversationId.value
);

const getAccessPermissionId = () => {
  try {
    const raw = localStorage.getItem("accessPermission");
    return raw ? JSON.parse(raw)?.id : null;
  } catch {
    return null;
  }
};

const loadIQConversations = async () => {
  const permissionId = getAccessPermissionId();
  let accountId = null;
  try {
    accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    accountId = null;
  }
  if (permissionId == null || permissionId === "" || accountId == null) {
    return;
  }
  try {
    const res = await fetchIQConversations(permissionId, accountId);
    iqConversationsData.value = res.data;
  } catch (error) {
    console.error("Failed to fetch IQ conversations:", error);
  }
};

const handleSidebarConversationSelect = async (conversationId) => {
  if (!conversationId) return;
  const permissionId = getAccessPermissionId();
  let accountId = null;
  try {
    accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    accountId = null;
  }
  if (permissionId == null || permissionId === "" || accountId == null) return;

  selectedSidebarConversationId.value = conversationId;
  iqConversationDetail.value = null;
  chatStore.clearChat();

  try {
    const res = await fetchIQConversationDetail(
      permissionId,
      conversationId,
      accountId
    );
    iqConversationDetail.value = res.data;
    const mapped = mapIQConversationDetailToChatMessages(res.data);
    chatStore.setMessages(mapped);
  } catch (error) {
    console.error("Failed to fetch IQ conversation:", error);
    iqConversationDetail.value = null;
  }
};

const handleSidebarNewChat = () => {
  selectedSidebarConversationId.value = null;
  iqConversationDetail.value = null;
  chatStore.clearChat();
};

/**
 * Contextual title for the IQControlledOverlayHeader (Untitled UI light overlay).
 * - Controlled suggestion (Mode A): describes the active suggestion task.
 * - General chat (Mode B): falls back to the welcome prompt.
 */
const overlayHeaderTitle = computed(() => {
  if (props.type === "roleResponsibility" || props.showSuggestions) {
    return props.roleName
      ? `Suggest Responsibilities for role ${props.roleName}`
      : "Suggest Responsibilities";
  }
  if (props.type === "responsibilityCheckpoint" || props.showCheckpointSuggestions) {
    return props.responsibilityName
      ? `Suggest Checkpoints for Responsibility ${props.responsibilityName}`
      : "Suggest Checkpoints";
  }
  if (props.type === "videoInstructions") {
    return "Suggest Video Instructions";
  }
  if (props.type === "positionImport") {
    return "Import Job Description";
  }
  return props.welcomeText || "What do you want to do today?";
});

const responsibilitySuggestionTranscriptEnabled = computed(() => {
  return store.getters["featureFlags/ideaMDP577"];
});

const iqSuggestExitConfirmEnabled = computed(() => {
  return store.getters["featureFlags/ideaMDP577"];
});

const shouldStartNewSuggestionSession = ref(false);

const isControlledSuggestOverlay = computed(() => isControlledIQSuggestOverlayType(props.type));

const isPositionImport = computed(() => props.type === "positionImport");

const usesSuggestChatInput = computed(() => isControlledSuggestOverlay.value);

const suggestChatInputIdlePlaceholder = computed(() => {
  if (props.type === "responsibilityCheckpoint") {
    return "Ask IQ anything about this responsibility…";
  }
  return "Ask IQ anything about this role…";
});

const suggestChatInputPlaceholder = computed(() => {
  if (chatStore.isSuggestionStreamActive) {
    const isRegeneration = chatStore.messages.some((message) => message.type === "ai");
    return isRegeneration ? "IQ is updating suggestions…" : "IQ is preparing suggestions…";
  }
  return suggestChatInputIdlePlaceholder.value;
});

const isAwaitingRegeneratedSuggestions = computed(() =>
  isAwaitingRegeneratedResponsibilitySuggestions({
    transcriptEnabled: responsibilitySuggestionTranscriptEnabled.value,
    overlayType: props.type,
    isSuggestionStreamActive: chatStore.isSuggestionStreamActive,
    unreviewedCount: props.suggestedResponsibilities?.length ?? 0,
    messages: chatStore.messages,
  })
);

const remainingSuggestions = computed(() => {
  if (props.type === "roleResponsibility" || props.showSuggestions) {
    return props.suggestedResponsibilities?.length ?? 0;
  }
  if (props.type === "responsibilityCheckpoint" || props.showCheckpointSuggestions) {
    return props.suggestedCheckpoints?.length ?? 0;
  }
  return 0;
});

const openingSuggestionPrompt = computed(() => props.initialPrompt || iqOverlayStore.initialPrompt);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      loadIQConversations();
      shouldStartNewSuggestionSession.value = isControlledSuggestOverlay.value;
      if (isPositionImport.value) {
        // `startPositionImportFlow` is declared later in this <script setup> block;
        // defer past setup so the const is initialized (avoids a TDZ ReferenceError).
        nextTick(() => startPositionImportFlow());
      }
    }
  },
  { immediate: true }
);

// Inspection Step Dialog state
const showInspectionDialog = ref(false);
const selectedStep = ref(null);
const selectedStepIndex = ref(-1);

// Computed: Check if video instructions has an error (validation failed)
const hasVideoInstructionsError = computed(() => {
  return !!(iqOverlayStore.suggestedInstructions && iqOverlayStore.suggestedInstructions.error);
});

// Feature flag for checkpoint inspection kit (ideaMDP427) – when on, also save to inspection instructions API
const checkpointInspectionKit = computed(() => {
  return store.getters["featureFlags/ideaMDP427"];
});

// Inspection Step Dialog computed properties
const inspectionDialogState = computed(() => {
  if (!selectedStep.value) return "Empty";
  return "Edit";
});

const dialogCategory = computed(() => {
  return selectedStep.value && selectedStep.value.competence_category
    ? selectedStep.value.competence_category
    : "knowledge";
});

const dialogInspect = computed(() => {
  return selectedStep.value && selectedStep.value.inspector_action
    ? selectedStep.value.inspector_action
    : "";
});

const dialogLookFor = computed(() => {
  return selectedStep.value && selectedStep.value.look_for ? selectedStep.value.look_for : "";
});

const dialogPassIf = computed(() => {
  return selectedStep.value && selectedStep.value.pass_criteria
    ? selectedStep.value.pass_criteria
    : "";
});

const dialogFailIf = computed(() => {
  return selectedStep.value && selectedStep.value.fail_criteria
    ? selectedStep.value.fail_criteria
    : "";
});

// Computed: Determine which loader to use
// Use custom loader if initialPrompt is provided (auto-filled), otherwise use pulse loader
const shouldUseCustomLoader = computed(() => {
  // If explicitly set via prop, use that value
  if (props.useCustomLoader !== undefined) {
    return props.useCustomLoader;
  }
  // Otherwise, use custom loader if initialPrompt is provided (auto-filled)
  return !!props.initialPrompt;
});

// Watch for initialPrompt and auto-submit when provided
watch(
  () => props.initialPrompt,
  (newPrompt) => {
    if (newPrompt && props.modelValue) {
      // Auto-submit the initial prompt
      nextTick(() => {
        handleSubmit(newPrompt);
      });
    }
  },
  { immediate: true }
);

const performClose = () => {
  emit("update:modelValue", false);
  emit("close");
  selectedSidebarConversationId.value = null;
  iqConversationDetail.value = null;
  imagePreview.value = null;
  positionImportSession.value = null;
  positionImportEditing.value = null;
  positionImportEditText.value = "";
  positionImportEditError.value = "";
  positionImportBusy.value = false;
  chatStore.clearChat();
};

const resetSuggestionSessionOnExit = () => {
  resetIQSuggestionSession({
    type: props.type,
    prompt: openingSuggestionPrompt.value,
    roleId: iqOverlayStore.roleId,
    responsibilityId: iqOverlayStore.responsibilityId,
  });
};

const { showExitConfirm, requestClose, keepReviewing, confirmExit } = useIQSuggestOverlayExitConfirm(
  {
    enabled: iqSuggestExitConfirmEnabled,
    isControlledSuggestOverlay,
    remainingSuggestions,
    resetSession: resetSuggestionSessionOnExit,
    performClose,
  }
);

const handleClose = () => {
  requestClose();
};

const openImagePreview = (payload) => {
  if (!payload?.src) {
    return;
  }
  imagePreview.value = payload;
};

const closeImagePreview = () => {
  imagePreview.value = null;
};

const clearUnreviewedSuggestionsForRefinement = () => {
  const unreviewedCount = props.suggestedResponsibilities?.length ?? 0;
  if (
    !shouldClearUnreviewedSuggestionsOnRefinement({
      transcriptEnabled: responsibilitySuggestionTranscriptEnabled.value,
      overlayType: props.type,
      messages: chatStore.messages,
      unreviewedCount,
    })
  ) {
    return;
  }

  isAllDone.value = false;
  emit("update:suggestions", {
    suggestions: [],
    roleName: props.roleName,
  });
};

const handleSubmit = async (data) => {
  clearUnreviewedSuggestionsForRefinement();
  chatStore.addUserMessage(data);

  // Determine which API endpoint to use based on type
  let apiEndpoint = null;
  let customPayload = null;

  // Get account_id from localStorage
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

  // Get the IQ base URL from config (comes from backend secure config)
  const iqBaseUrl = (configService.get("iqBaseUrl") || "").trim().replace(/\/$/, "");

  if (props.type === "videoInstructions") {
    const ideaMDP452 = !!store.getters["featureFlags/ideaMDP452"];
    apiEndpoint = buildIqVideoSuggestEndpoint(iqBaseUrl, ideaMDP452);

    const extractedVideoUrl = extractVideoUrl(iqOverlayStore.videoUrl);

    const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
    const businessPurpose = currentAccountDetails.purpose || "";

    const checkpointCriteria =
      `${iqOverlayStore.checkpointPrefix} ${iqOverlayStore.checkpointCriteria}`.trim();

    customPayload = buildVideoToComponentsPayload({
      videoUrl: extractedVideoUrl,
      businessPurpose,
      responsibilityName: iqOverlayStore.responsibilityName || "",
      responsibilityPurpose: iqOverlayStore.responsibilityPurpose || "",
      checkpointCriteria,
      language: "english",
      microlearningSelection: iqOverlayStore.videoToComponentsSelection,
    });
  } else if (props.type === "roleResponsibility") {
    apiEndpoint = `${iqBaseUrl}/${API.GET_SUGGESTIONS}`;

    const startNew = shouldStartNewSuggestionSession.value;
    if (startNew) {
      shouldStartNewSuggestionSession.value = false;
    }

    customPayload = buildIQSuggestionPayload({
      prompt: data,
      suggestionType: suggestionTypeForOverlayType(props.type),
      roleId: iqOverlayStore.roleId,
      startNew,
    });
  } else if (props.type === "responsibilityCheckpoint") {
    apiEndpoint = `${iqBaseUrl}/${API.GET_SUGGESTIONS}`;

    const startNew = shouldStartNewSuggestionSession.value;
    if (startNew) {
      shouldStartNewSuggestionSession.value = false;
    }

    customPayload = buildIQSuggestionPayload({
      prompt: data,
      suggestionType: suggestionTypeForOverlayType(props.type),
      responsibilityId: iqOverlayStore.responsibilityId,
      startNew,
    });
  }

  const conversationIdForAsk =
    !apiEndpoint && !customPayload ? selectedSidebarConversationId.value : null;

  await chatStore.generateAIResponse(
    data,
    5,
    apiEndpoint,
    customPayload,
    loadIQConversations,
    conversationIdForAsk
  );
};

// ---------- Import Job Description (positionImport) flow ----------

const postPositionImportMessages = (messages) => {
  (messages || []).forEach((message) => {
    chatStore.addAIMessage(message);
  });
};

const startPositionImportFlow = () => {
  chatStore.clearChat();
  positionImportSession.value = createPositionImportSession({
    positionId: iqOverlayStore.positionId,
    positionName: iqOverlayStore.positionName || props.roleName,
  });
  positionImportEditing.value = null;
  positionImportEditText.value = "";
  positionImportEditError.value = "";
  postPositionImportMessages([positionImportSession.value.initialMessage()]);
};

const handlePositionImportChooseFile = () => {
  if (positionImportFileInput.value) {
    positionImportFileInput.value.click();
  }
};

const handlePositionImportFileSelected = async (event) => {
  const file = event?.target?.files?.[0];
  event.target.value = "";
  if (!file || !positionImportSession.value) return;

  // Surface the uploaded document so the ImportJobDescriptionIQOverlay wrapper
  // can render the pinned file card + document preview (ER-11580).
  emit("document-uploaded", { name: file.name, type: file.type });

  positionImportBusy.value = true;
  try {
    const result = await extractPositionImportResponsibilities({
      positionId: positionImportSession.value.state.positionId,
      file,
    });
    const messages = positionImportSession.value.applyExtraction({
      bullets: result?.bullets || [],
      titleMismatch: result?.title_mismatch,
      jdTitle: result?.jd_title || result?.jdTitle,
    });
    postPositionImportMessages(messages);

    // Rule 4a — a zero-bullet result means the document is not a job
    // description. The session stays open so "Upload a different file" can
    // re-trigger extraction within this same overlay session.
  } catch (error) {
    console.error("Failed to extract position import responsibilities:", error);
    chatStore.addAIMessage(
      "I'm sorry, I couldn't read that document. Please try again with a .docx or .pdf file."
    );
  } finally {
    positionImportBusy.value = false;
  }
};

const handlePositionImportCancel = () => {
  // Rule 3a — cancel ends the session immediately, no extraction shown.
  positionImportSession.value = null;
  positionImportEditing.value = null;
  handleClose();
};

const handlePositionImportTitleMismatch = (choice) => {
  if (!positionImportSession.value) return;
  if (choice === POSITION_IMPORT_ACTION.CANCEL_IMPORT) {
    handlePositionImportCancel();
    return;
  }
  postPositionImportMessages(positionImportSession.value.resolveTitleMismatch(choice));
};

const handlePositionImportOptIn = async (choice) => {
  if (!positionImportSession.value) return;
  if (choice === POSITION_IMPORT_ACTION.BENCHMARK_NO) {
    postPositionImportMessages(positionImportSession.value.resolveOptIn(choice));
    return;
  }

  // Rule 15 — invoke benchmark_improvement per extracted bullet, then review
  // only the bullets that actually received a rewrite.
  positionImportBusy.value = true;
  try {
    const session = positionImportSession.value;
    const improvements = [];
    for (const bullet of session.state.bullets) {
      const payload = buildPositionImportBenchmarkPayload({
        prompt: bullet,
        suggestionType: POSITION_IMPORT_SUGGESTION_TYPES.IMPROVEMENT,
        positionId: session.state.positionId,
        positionTitle: session.state.positionName,
      });
      const iqBaseUrl = (configService.get("iqBaseUrl") || "").trim().replace(/\/$/, "");
      const endpoint = `${iqBaseUrl}/${API.GET_SUGGESTIONS}`;
      const response = await askIQStream({
        question: bullet,
        customEndpoint: endpoint,
        customPayload: payload,
      });
      const rewrite = normalizePositionImportRewrite(response?.data, bullet);
      if (rewrite && rewrite !== bullet) {
        improvements.push({ original: bullet, rewrite });
      }
    }
    session.resolveOptIn(choice);
    const messages = session.loadImprovements(improvements);
    if (messages.length === 0) {
      // No rewrites warranted → skip straight to Rule 16.
      await handlePositionImportRunMissing(session);
      return;
    }
    postPositionImportMessages(messages);
  } catch (error) {
    console.error("Failed to load benchmark improvements:", error);
    chatStore.addAIMessage(
      "I'm sorry, I couldn't run the best-practices comparison right now. Please try again."
    );
  } finally {
    positionImportBusy.value = false;
  }
};

const normalizePositionImportRewrite = (data, original) => {
  if (!data) return null;
  const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
  const first = suggestions[0];
  const rewrite =
    first?.rewrite || first?.improved || first?.text || first?.suggestion || data.answer || null;
  if (rewrite && rewrite !== original) {
    return rewrite;
  }
  return null;
};

const handlePositionImportRunMissing = async (session) => {
  positionImportBusy.value = true;
  try {
    const payload = buildPositionImportBenchmarkPayload({
      prompt: session.state.positionName,
      suggestionType: POSITION_IMPORT_SUGGESTION_TYPES.MISSING,
      positionId: session.state.positionId,
      positionTitle: session.state.positionName,
    });
    const iqBaseUrl = (configService.get("iqBaseUrl") || "").trim().replace(/\/$/, "");
    const endpoint = `${iqBaseUrl}/${API.GET_SUGGESTIONS}`;
    const response = await askIQStream({
      question: session.state.positionName,
      customEndpoint: endpoint,
      customPayload: payload,
    });
    const missing = Array.isArray(response?.data?.suggestions)
      ? response.data.suggestions.map((item) =>
          item?.text || item?.suggestion || item?.name || item?.answer || ""
        ).filter(Boolean)
      : [];
    const messages = session.loadMissing(missing);
    if (messages.length === 0) {
      // No missing responsibilities proposed → straight to the readiness gate.
      session.state.phase = POSITION_IMPORT_PHASE.GATE;
      postPositionImportMessages([buildReadinessGateMessage()]);
      return;
    }
    postPositionImportMessages(messages);
  } catch (error) {
    console.error("Failed to load benchmark missing responsibilities:", error);
    chatStore.addAIMessage(
      "I'm sorry, I couldn't load the missing-responsibilities comparison right now. Please try again."
    );
  } finally {
    positionImportBusy.value = false;
  }
};

const handlePositionImportImprove = () => {
  const session = positionImportSession.value;
  if (!session) return;
  const kind =
    session.state.phase === POSITION_IMPORT_PHASE.IMPROVEMENTS ? "improvement" : "missing";
  const current =
    kind === "improvement" ? session.currentImprovement() : session.currentMissing();
  if (!current) return;
  positionImportEditing.value = { kind, current };
  positionImportEditText.value =
    kind === "improvement" ? current.rewrite : current.text;
  positionImportEditError.value = "";
};

const handlePositionImportEditSave = async () => {
  const text = (positionImportEditText.value || "").trim();
  if (!text) {
    positionImportEditError.value = "Please enter the responsibility text.";
    return;
  }
  positionImportEditing.value = null;
  positionImportEditText.value = "";
  positionImportEditError.value = "";
  await applyPositionImportReview(POSITION_IMPORT_ACTION.LOVE, text);
};

const handlePositionImportEditCancel = () => {
  positionImportEditing.value = null;
  positionImportEditText.value = "";
  positionImportEditError.value = "";
};

const applyPositionImportReview = async (choice, editedText = null) => {
  const session = positionImportSession.value;
  if (!session) return;
  const isImprovement = session.state.phase === POSITION_IMPORT_PHASE.IMPROVEMENTS;

  if (isImprovement) {
    const messages = session.resolveImprovement(choice, editedText);
    if (messages.length === 0 && session.currentImprovement() == null) {
      // Improvements exhausted → Rule 16.
      await handlePositionImportRunMissing(session);
      return;
    }
    postPositionImportMessages(messages);
    return;
  }

  const messages = session.resolveMissing(choice, editedText);
  postPositionImportMessages(messages);
};

const handlePositionImportReview = async (action) => {
  positionImportEditing.value = null;
  positionImportEditError.value = "";

  if (action.type === POSITION_IMPORT_ACTION.IMPROVE) {
    handlePositionImportImprove();
    return;
  }
  await applyPositionImportReview(action.type);
};

const handlePositionImportGate = (choice) => {
  const session = positionImportSession.value;
  if (!session) return;
  session.resolveGate(choice);
  if (session.state.phase === POSITION_IMPORT_PHASE.DONE) {
    // Figma 47631:378821 matching loop (ask for a solution per bullet):
    //   no match | one match unlinked | one match + 1 role | one match + N roles
    //   then post the chosen solution and show IQ's execution reply.
    // IQ `position_import_match` already returns match / no-match. Covering
    // roles and execute/attach/create are not in IQ yet — backend will be in
    // IQ services. Until then this session ends with the working bullet list.
    chatStore.addAIMessage(
      "Import confirmed. Your extracted responsibilities are ready for matching."
    );
    positionImportSession.value = null;
    return;
  }
  // "Not yet" keeps the gate open.
  postPositionImportMessages([buildReadinessGateMessage()]);
};

// Extract video URL from iframe HTML format
// Input: <iframe src="https://embed.api.video/vod/vi4F5DkE4590VkCwsOMfRL5w" ...></iframe>
// Output: https://embed.api.video/vod/vi4F5DkE4590VkCwsOMfRL5w
const extractVideoUrl = (iframeHtml) => {
  if (!iframeHtml) return "";

  // If it's already a direct URL (not iframe), return as is
  if (!iframeHtml.includes("<iframe") && !iframeHtml.includes("src=")) {
    return iframeHtml;
  }

  // Extract src from iframe - handle both escaped quotes \" and regular quotes "
  const srcMatch = iframeHtml.match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) {
    return srcMatch[1];
  }

  // Fallback: try without escaped quotes
  const fallbackMatch = iframeHtml.match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) {
    return fallbackMatch[1];
  }

  return "";
};

const handleStop = () => {
  stopProgressAnimation();
  hideLoader();
  chatStore.stopGenerating();
};

const handleLoaderStop = () => {
  // Immediately hide loader and stop animation
  stopProgressAnimation();
  hideLoader();
  // Cancel the API request
  chatStore.stopGenerating();
};

// Loader visibility management
const showLoader = () => {
  loaderVisible.value = true;
};

const hideLoader = () => {
  loaderVisible.value = false;
};

// Progress animation constants
const PROGRESS_ANIMATION = {
  TARGET_TIME_MS: 30000, // 30 seconds to reach initial progress threshold
  INITIAL_MAX_PROGRESS: 92, // Cap at 92% until API responds
  FINAL_MAX_PROGRESS: 95, // Cap at 95% during slow crawl phase
  SLOW_CRAWL_DURATION_MS: 20000, // Duration for slow crawl phase
  SLOW_CRAWL_INCREMENT: 3, // Additional progress during slow crawl (92% to 95%)
  UPDATE_INTERVAL_MS: 50, // Update every 50ms for smoother animation
};

// Progress animation logic - reaches ~90% over ~30 seconds, then slows down
const startProgressAnimation = () => {
  stopProgressAnimation(); // Clear any existing interval
  loadingProgress.value = 0;
  showLoader();

  const startTime = Date.now();
  const targetTime = PROGRESS_ANIMATION.TARGET_TIME_MS;
  const maxProgress = PROGRESS_ANIMATION.INITIAL_MAX_PROGRESS;

  progressInterval.value = setInterval(() => {
    if (!chatStore.isLoading || chatStore.currentResponse) {
      stopProgressAnimation();
      return;
    }

    const elapsed = Date.now() - startTime;

    if (elapsed < targetTime) {
      // First phase: reach ~90% over 30 seconds
      // Use easing function for smooth animation
      const progress = (elapsed / targetTime) * maxProgress;
      loadingProgress.value = Math.min(progress, maxProgress);
    } else {
      // Second phase: slow crawl from 92% to 95% over additional time
      // This keeps the bar moving while waiting for API response
      const extraTime = elapsed - targetTime;
      const slowProgress =
        maxProgress +
        Math.min(
          (extraTime / PROGRESS_ANIMATION.SLOW_CRAWL_DURATION_MS) *
            PROGRESS_ANIMATION.SLOW_CRAWL_INCREMENT,
          PROGRESS_ANIMATION.SLOW_CRAWL_INCREMENT
        );
      loadingProgress.value = Math.min(slowProgress, PROGRESS_ANIMATION.FINAL_MAX_PROGRESS);
    }
  }, PROGRESS_ANIMATION.UPDATE_INTERVAL_MS);
};

const stopProgressAnimation = () => {
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
    progressInterval.value = null;
  }
  loadingProgress.value = 0;
};

const handleActionClick = (action) => {
  // Import Job Description (positionImport) actions
  if (isPositionImport.value) {
    const type = action?.type;
    if (type === POSITION_IMPORT_ACTION.CHOOSE_FILE) {
      handlePositionImportChooseFile();
      return;
    }
    if (type === POSITION_IMPORT_ACTION.UPLOAD_DIFFERENT_FILE) {
      handlePositionImportChooseFile();
      return;
    }
    if (type === POSITION_IMPORT_ACTION.ABORT) {
      handlePositionImportCancel();
      return;
    }
    if (type === POSITION_IMPORT_ACTION.CANCEL_IMPORT || type === POSITION_IMPORT_ACTION.CONTINUE_AS_IS) {
      handlePositionImportTitleMismatch(type);
      return;
    }
    if (type === POSITION_IMPORT_ACTION.BENCHMARK_YES || type === POSITION_IMPORT_ACTION.BENCHMARK_NO) {
      handlePositionImportOptIn(type);
      return;
    }
    if (
      type === POSITION_IMPORT_ACTION.IMPROVE ||
      type === POSITION_IMPORT_ACTION.LOVE ||
      type === POSITION_IMPORT_ACTION.SKIP
    ) {
      handlePositionImportReview(action);
      return;
    }
    if (type === POSITION_IMPORT_ACTION.PROCEED || type === POSITION_IMPORT_ACTION.NOT_YET) {
      handlePositionImportGate(type);
      return;
    }
  }

  // Handle review-suggestions action to refresh responsibility suggestions
  if (action.type === "review-suggestions") {
    // First, close the navigator
    emit("close-navigator");

    // Then submit a new query to refresh suggestions with role info
    nextTick(() => {
      const refreshPrompt = `Pls refresh suggestions for Role ${props.roleName}`;
      handleSubmit(refreshPrompt);
    });
  }

  // Handle view-video-instructions action to show the right pane
  if (action.type === "view-video-instructions") {
    // Show video instructions pane
    iqOverlayStore.showVideoInstructionsPane = true;
  }

  // Handle refresh-checkpoint-suggestions action to refresh checkpoint suggestions
  if (action.type === "refresh-checkpoint-suggestions") {
    // First, close the checkpoint navigator
    emit("close-checkpoint-navigator");
    iqOverlayStore.closeCheckpointNavigator();

    // Then submit a new query to refresh checkpoint suggestions
    nextTick(() => {
      const refreshPrompt = `Please suggest more Checkpoints for Responsibility ${props.responsibilityName}`;
      handleSubmit(refreshPrompt);
    });
  }

  // Emit action to parent for handling
  emit("actionClick", action);
};

const handleNavigatorClose = () => {
  emit("close-navigator");
};

const handleCreateAndAttach = (data) => {
  emit("create-and-attach", data);
};

const handleReject = (data) => {
  emit("reject", data);
};

const handleSuggestMore = () => {
  // Close the navigator first
  emit("close-navigator");

  // Submit refresh query to get new suggestions with role info
  nextTick(() => {
    const refreshPrompt = `Pls refresh suggestions for Role ${props.roleName}`;
    handleSubmit(refreshPrompt);
  });

  emit("suggest-more");
};

const handleEdit = (data) => {
  emit("edit", data);
};

const handleIsAllDone = (value) => {
  isAllDone.value = value;
};

// Video instructions handlers
const handleVideoInstructionsClose = () => {
  iqOverlayStore.closeVideoInstructionsPane();
};

const handleVideoInstructionsSave = async () => {
  // Get account_id from localStorage
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const responsibilityId = iqOverlayStore.responsibilityId;
  const checkpointId = iqOverlayStore.checkpointId;
  const instructionsData = iqOverlayStore.suggestedInstructions;
  if (!accountId || !responsibilityId || !checkpointId) {
    console.error("Missing required IDs for saving video instructions");
    return;
  }

  try {
    // Prepare the microlearning_instructions payload
    const payload = {
      microlearning_instructions: instructionsData,
    };
    isButtonLoading.value = true;
    await changeCheckpointV2(accountId, responsibilityId, checkpointId, payload);

    // When checkpoint inspection kit flag is on, also save to video inspection instructions API
    if (checkpointInspectionKit.value) {
      await saveVideoInspectionInstructions(accountId, responsibilityId, checkpointId, {
        inspection_instructions: instructionsData,
      });
    }

    // Emit refresh event via store to update checkpoint data
    iqOverlayStore.emitRefreshCheckpoint();
    // Also emit via component event
    emit("refresh-checkpoint");
    // Close the overlay after successful save
    handleClose();
  } catch (error) {
    isButtonLoading.value = false;
    console.error("Error saving video instructions:", error);
    // Still close the overlay on error
    handleClose();
  } finally {
    isButtonLoading.value = false;
  }
};

const handleVideoInstructionsDiscard = () => {
  // Discard changes and close the entire overlay
  handleClose();
};

const handleSummaryClick = (summary) => {
  console.log("Summary clicked:", summary);
  // Future: open summary edit dialog
};

const handleStepClick = ({ step, index }) => {
  // Open inspection step dialog for editing
  selectedStep.value = { ...step };
  selectedStepIndex.value = index;
  showInspectionDialog.value = true;
};

// Checkpoint Navigator Handlers
const handleCheckpointNavigatorClose = () => {
  emit("close-checkpoint-navigator");
  iqOverlayStore.closeCheckpointNavigator();
};

const handleAddCheckpoint = (data) => {
  // Only emit through the store for subscribers (EditCheckpointsTab)
  // Don't use Vue emit as it's not handled by App.vue and causes duplicate calls
  iqOverlayStore.emitAddCheckpoint(data);
};

const handleRejectCheckpoint = (data) => {
  // Only emit through the store for subscribers
  iqOverlayStore.emitRejectCheckpoint(data);
};

const handleSuggestMoreCheckpoints = () => {
  // Close the navigator first
  emit("close-checkpoint-navigator");
  iqOverlayStore.closeCheckpointNavigator();

  // Submit refresh query to get new suggestions with responsibility info
  nextTick(() => {
    const refreshPrompt = `Please suggest more Checkpoints for Responsibility ${props.responsibilityName}`;
    handleSubmit(refreshPrompt);
  });

  // Only emit through the store for subscribers
  iqOverlayStore.emitSuggestMoreCheckpoints();
};

const handleEditCheckpoint = (data) => {
  emit("edit-checkpoint", data);
};

const handleIsAllCheckpointsDone = (value) => {
  isAllCheckpointsDone.value = value;
};

// Inspection Step Dialog handlers
const handleInspectionDialogClose = () => {
  showInspectionDialog.value = false;
  selectedStep.value = null;
  selectedStepIndex.value = -1;
};

const handleInspectionDialogDiscard = () => {
  // Just close without saving
  handleInspectionDialogClose();
};

const handleInspectionDialogSubmit = (formData) => {
  // Update the step in the store's suggestedInstructions
  if (
    iqOverlayStore.suggestedInstructions &&
    selectedStepIndex.value >= 0 &&
    iqOverlayStore.suggestedInstructions.steps &&
    selectedStepIndex.value < iqOverlayStore.suggestedInstructions.steps.length
  ) {
    // Create updated instructions object with the modified step
    const updatedInstructions = {
      ...iqOverlayStore.suggestedInstructions,
      steps: [
        ...iqOverlayStore.suggestedInstructions.steps.slice(0, selectedStepIndex.value),
        {
          ...iqOverlayStore.suggestedInstructions.steps[selectedStepIndex.value],
          competence_category: formData.category,
          inspector_action: formData.inspect,
          look_for: formData.lookFor,
          pass_criteria: formData.passIf,
          fail_criteria: formData.failIf,
        },
        ...iqOverlayStore.suggestedInstructions.steps.slice(selectedStepIndex.value + 1),
      ],
    };

    // Update the store (this will reflect in MicrolearningViewEdit component)
    iqOverlayStore.suggestedInstructions = updatedInstructions;
  }

  // Close the dialog
  handleInspectionDialogClose();
};

// Auto scroll to bottom when new messages arrive
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

// Watch for new messages and scroll
watch(() => chatStore.messages.length, scrollToBottom);
watch(() => chatStore.currentResponse, scrollToBottom);

// Watch for loading state to start/stop progress animation (only for custom loader)
watch(
  () => chatStore.isLoading,
  (isLoading, wasLoading) => {
    if (shouldUseCustomLoader.value) {
      if (isLoading && !chatStore.currentResponse && chatStore.messages.length > 0) {
        startProgressAnimation();
      } else if (!isLoading && wasLoading) {
        // API call completed - complete the progress bar
        if (loadingProgress.value < 100) {
          loadingProgress.value = 100;
        }
        setTimeout(() => {
          stopProgressAnimation();
          hideLoader();
        }, 200); // Small delay to show completion
      } else if (!isLoading) {
        stopProgressAnimation();
        hideLoader();
      }
    }
  }
);

// Watch for currentResponse to stop animation when response arrives (only for custom loader)
watch(
  () => chatStore.currentResponse,
  (response, oldResponse) => {
    if (shouldUseCustomLoader.value && response && !oldResponse) {
      // Response just arrived - complete the progress bar to 100%
      loadingProgress.value = 100;
      setTimeout(() => {
        stopProgressAnimation();
        hideLoader();
      }, 200); // Small delay to show completion
    }
  }
);

// Cleanup on unmount
onUnmounted(() => {
  stopProgressAnimation();
});

// Watch for when generation completes to auto-open navigator
watch(
  () => chatStore.isGenerating,
  (isGenerating, wasGenerating) => {
    // When generation completes (was generating, now not)
    if (wasGenerating && !isGenerating) {
      // Get the last AI message (the one just added)
      const lastMessage = chatStore.messages.filter((msg) => msg.type === "ai").pop();

      // Check if we have video instruction suggestions
      if (
        lastMessage &&
        lastMessage.content &&
        lastMessage.content.suggestions &&
        lastMessage.content.suggestions.steps &&
        Array.isArray(lastMessage.content.suggestions.steps)
      ) {
        // This is a video instructions response - set the instructions and show pane
        nextTick(() => {
          iqOverlayStore.setSuggestedInstructions(lastMessage.content.suggestions);
        });
        return;
      }

      // Check if we have suggestions in the last message
      if (
        lastMessage &&
        lastMessage.content &&
        lastMessage.content.suggestions &&
        Array.isArray(lastMessage.content.suggestions) &&
        lastMessage.content.suggestions.length > 0
      ) {
        // Determine type from the response suggestionType
        const suggestionType = lastMessage.content.suggestionType;

        // Handle based on suggestion type from the response
        if (suggestionType === "responsibility_checkpoint_criteria") {
          // Transform suggestions to the format expected by SuggestCheckpointsNavigator
          // Map proficiency_level (1, 2, 3, 4) to level names
          const levelMap = {
            1: "Apprentice",
            2: "Professional",
            3: "Coach",
            4: "Master",
          };

          const transformedCheckpoints = lastMessage.content.suggestions.map((item) => ({
            criteria: item.criteria || item.checkpoint_criteria || "",
            // Map 'purpose' from backend to 'instructions' for the navigator
            instructions:
              item.instruction ||
              item.instructions ||
              item.purpose ||
              item.checkpoint_instructions ||
              "",
            level: levelMap[item.proficiency_level] || item.level || "Apprentice",
            // Include prefix from backend response
            prefix: item.prefix || "",
          }));

          // Emit to parent to update checkpoint suggestions and show navigator
          nextTick(() => {
            emit("update:checkpoint-suggestions", {
              suggestions: transformedCheckpoints,
              responsibilityName: props.responsibilityName,
            });
          });
        } else {
          const transformedSuggestions = mapRoleResponsibilitySuggestions(
            lastMessage.content.suggestions
          );

          // Emit to parent to update suggestions and show navigator
          nextTick(() => {
            emit("update:suggestions", {
              suggestions: transformedSuggestions,
              roleName: props.roleName, // Always use the original roleName prop, not the API response
            });
          });
        }
      } else {
        console.log("No suggestions found in last message");
      }
    }
  }
);
</script>

<style>
.iq-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.iq-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f9fafb;
}

.iq-overlay-shell {
  position: relative;
  z-index: 10;
  flex: 1;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
}

.iq-overlay-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* Header */
.iq-overlay-header {
  position: relative;
  width: 100%;
  z-index: 10;
  background: #ffffff;
  flex-shrink: 0;
}

/* Body Area */
.iq-overlay-body {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  z-index: 1;
  isolation: isolate;
}

.iq-overlay-body.split-layout {
  display: flex;
  flex-direction: row;
}

.iq-overlay.mdp-577-transcript .iq-overlay-body.split-layout {
  gap: 10px;
}

/* Chat Section */
.iq-chat-section {
  width: 100%;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
}

.iq-chat-section.with-suggestions {
  width: 50%;
  flex: 0 0 50%;
}

.iq-chat-section.with-video-instructions {
  width: 30%;
  flex: 0 0 30%;
}

.iq-chat-section.with-video-instructions-error {
  width: 50%;
  flex: 0 0 50%;
}

.iq-chat-section.with-document-preview {
  width: 50%;
  flex: 0 0 50%;
}

/* Suggestions Section */
.iq-suggestions-section {
  width: 50%;
  flex: 0 0 50%;
  height: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
}

.iq-suggestions-section .responsibility-suggestion-modal {
  height: 86vh !important;
}

.iq-overlay.mdp-577-transcript .iq-suggestions-section .responsibility-suggestion-modal {
  height: 90vh !important;
}

.iq-suggestions-section.all-done .responsibility-suggestion-modal {
  height: auto !important;
  min-height: fit-content;
}

.iq-suggestions-regenerating {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 100%;
  min-height: 240px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-sizing: border-box;
}

.iq-suggestions-regenerating__text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #667085;
  text-align: center;
}

.iq-suggestions-regenerating__pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #7f56d9;
  animation: iq-suggestions-regenerating-pulse 1.2s ease-in-out infinite;
}

@keyframes iq-suggestions-regenerating-pulse {
  0%,
  100% {
    transform: scale(0.85);
    opacity: 0.45;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Checkpoint suggestion modal styles */
.iq-suggestions-section .checkpoint-suggestion-modal {
  height: 86vh !important;
}

.iq-suggestions-section.all-done .checkpoint-suggestion-modal {
  height: auto !important;
  min-height: fit-content;
}

/* Navigator container - fills the right section */
#iq-navigator-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

.iq-overlay.mdp-577-transcript #iq-navigator-container {
  padding-top: 8px;
}

/* Checkpoint Navigator container - fills the right section */
#iq-checkpoint-navigator-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

/* Welcome State - Centered Layout */
.iq-welcome-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  max-width: 650px;
  margin: 0 auto;
  width: 100%;
}

.iq-welcome-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 650px;
}

.iq-search-center {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Chat State - Full Height Layout */
.iq-chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 650px;
  margin: 0 auto;
  width: 100%;
}

.iq-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;
  /* Hide scrollbar but keep scrolling functionality */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* Internet Explorer 10+ */
}

.iq-chat-messages::-webkit-scrollbar {
  display: none;
  /* WebKit browsers (Chrome, Safari, Edge) */
}

/* Pinned uploaded-file card (ER-11580) */
.iq-uploaded-file-card {
  flex-shrink: 0;
  width: 100%;
}

.iq-message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.iq-message-wrapper.user {
  align-items: flex-end;
}

.iq-message-wrapper.ai {
  align-items: flex-start;
}

.iq-loader-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 0;
  margin-left: 0;
}

.iq-loading-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
}

.iq-pulse-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #667085;
  animation: pulse 0.8s infinite ease-in-out;
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(0.85);
    opacity: 0.6;
  }

  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}

.iq-search-container {
  padding: 16px 20px 20px 20px;
  background: #ffffff;
  display: flex;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.iq-suggest-chat-input {
  width: 100%;
  max-width: none;
}

.iq-search-center .iq-suggest-chat-input {
  width: 100%;
  max-width: none;
}

/* Video Instructions Section */
.iq-video-instructions-section {
  width: calc(70% - 24px);
  flex: 0 0 calc(70% - 24px);
  height: calc(100% - 24px);
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  margin-right: 24px;
  margin-bottom: 24px;
}

/* Error state: 50-50 width, no header */
.iq-video-instructions-section--error {
  width: calc(50% - 24px);
  flex: 0 0 calc(50% - 24px);
}

.iq-video-instructions-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 20px 24px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;
}

.iq-video-instructions-title {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  margin: 0;
}

.iq-video-instructions-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.iq-video-instructions-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #667085;
  cursor: pointer;
  border-radius: 6px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.iq-video-instructions-close:hover {
  background-color: #f2f4f7;
  color: #344054;
}

.iq-video-instructions-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

/* Import Job Description document preview (ER-11580) */
.iq-document-preview-section {
  width: 50%;
  flex: 0 0 50%;
  height: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #eaecf0;
}

.iq-document-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;
}

.iq-document-preview-title {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.iq-document-preview-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #667085;
  cursor: pointer;
  border-radius: 6px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.iq-document-preview-close:hover {
  background-color: #f2f4f7;
  color: #344054;
}

.iq-document-preview-close:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.iq-document-preview-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}

.position-import-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.position-import-inline-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fcfcfd;
  margin-bottom: 12px;
}

.position-import-inline-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
